import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, CheckCircle, AlertTriangle, Download, Printer} from 'lucide-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface Question {
  id: number
  text: string
}

const panicQuestions: Question[] = [
  { id: 1, text: 'I experience sudden episodes of intense fear or discomfort' },
  { id: 2, text: 'During these episodes, my heart races or pounds' },
  { id: 3, text: 'I experience chest pain or discomfort during panic episodes' },
  { id: 4, text: 'I have difficulty breathing or feel like I\'m choking' },
  { id: 5, text: 'I feel dizzy, lightheaded, or like I might faint' },
  { id: 6, text: 'I worry about having another panic attack' },
  { id: 7, text: 'I avoid places or situations where I\'ve had panic attacks before' },
  { id: 8, text: 'I fear losing control or "going crazy" during a panic attack' }
]

const scoreOptions = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Very Often' }
]

export default function PanicDisorderAssessment() {
  const [answers, setAnswers] = useState<{ [key: number]: number }>({})
  const [showResults, setShowResults] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers({ ...answers, [questionId]: value })
  }

  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0)
  }

  const handleSubmit = () => {
    if (Object.keys(answers).length === 8) {
      setShowResults(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = async () => {
    if (!resultsRef.current) return
    
    setIsGeneratingPDF(true)
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      })
      
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`Panic-Disorder-Results-${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again or use the Print option.')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const getScoreInterpretation = (score: number) => {
    if (score <= 8) {
      return {
        level: 'Low Panic Symptoms',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Your responses suggest minimal panic symptoms. You may experience occasional anxiety, but not panic disorder.',
        recommendation: 'Continue healthy stress management practices. If you experience sudden panic symptoms, consult a healthcare provider.'
      }
    } else if (score <= 16) {
      return {
        level: 'Mild Panic Symptoms',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'Your responses indicate mild panic symptoms. You may be experiencing occasional panic attacks or anticipatory anxiety.',
        recommendation: 'Learn panic attack coping strategies (breathing exercises, grounding techniques). Consider consultation if symptoms increase or cause avoidance behaviors.'
      }
    } else if (score <= 24) {
      return {
        level: 'Moderate Panic Disorder',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Your responses suggest moderate panic disorder symptoms that may be limiting your activities.',
        recommendation: 'We strongly recommend consulting with a mental health professional. Panic disorder is highly treatable with Cognitive Behavioral Therapy (CBT) and/or medication (SSRIs).'
      }
    } else {
      return {
        level: 'Severe Panic Disorder',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Your responses indicate severe panic disorder that significantly impacts your daily life.',
        recommendation: 'Please consult with a mental health professional as soon as possible. Panic disorder is treatable with evidence-based therapies (CBT, panic-focused therapy) and medication. Many people achieve full remission with proper treatment.'
      }
    }
  }

  const score = calculateScore()
  const interpretation = getScoreInterpretation(score)
  const isComplete = Object.keys(answers).length === 8

  return (
    <div>
      {/* Results Section */}
      {showResults && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6 print:hidden">
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </button>
            </div>
            
            <div ref={resultsRef} className={`${interpretation.bgColor} border-2 ${interpretation.borderColor} rounded-xl p-8`}>
              <div className="text-center mb-6">
                <div className="text-6xl font-bold mb-2 text-gray-900">{score}</div>
                <div className={`text-2xl font-bold ${interpretation.color}`}>{interpretation.level}</div>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Results</h3>
                <p className="text-gray-700 mb-4">{interpretation.description}</p>
                <p className="text-gray-700"><strong>Recommendation:</strong> {interpretation.recommendation}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
                <Link
                  to="/therapy"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all"
                >
                  Find a Therapist
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <button
                  onClick={() => {
                    setAnswers({})
                    setShowResults(false)
                  }}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all"
                >
                  Retake Assessment
                </button>
              </div>
              
              <div className="hidden print:block mt-8 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-600 text-center">
                  Generated by Anxiety USA - anxietyusa.com | Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About Panic Disorder */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About Panic Disorder Assessment</h2>
          <div className="prose prose-lg max-w-none text-gray-700 mb-8">
            <p className="mb-4">
              <strong>Panic Disorder</strong> is characterized by recurrent, unexpected panic attacks—sudden episodes of intense fear that reach a peak within minutes. Between 2-3% of Americans experience panic disorder each year.
            </p>
            <p className="mb-4">
              This screening assesses both the occurrence of panic attacks and the behavioral changes (avoidance, worry) that often develop in response to them. The combination of both is key to identifying panic disorder.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-[#4A90E2] p-6 rounded-r-lg mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Understanding Your Score</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="font-bold text-green-600 w-20 flex-shrink-0">0-8:</div>
                <div className="text-gray-700">Low panic symptoms</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-yellow-600 w-20 flex-shrink-0">9-16:</div>
                <div className="text-gray-700">Mild panic symptoms</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-orange-600 w-20 flex-shrink-0">17-24:</div>
                <div className="text-gray-700">Moderate panic disorder</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-red-600 w-20 flex-shrink-0">25-32:</div>
                <div className="text-gray-700">Severe panic disorder</div>
              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h4 className="font-bold text-gray-900 mb-3">Common Panic Attack Symptoms</h4>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Rapid heartbeat or palpitations
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Sweating and trembling
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Shortness of breath
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Chest pain or discomfort
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Nausea or stomach distress
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Feeling detached from reality
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Fear of dying or losing control
              </li>
              <li className="flex items-start">
                <span className="text-[#48C9B0] mr-2">•</span>
                Numbness or tingling sensations
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Panic Disorder Questionnaire */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Panic Disorder Severity Scale</h2>
            <p className="text-center text-gray-600 mb-8">
              Over the last month, how often have you experienced the following?
            </p>

            <div className="space-y-8">
              {panicQuestions.map((question) => (
                <div key={question.id} className="pb-6 border-b border-gray-200 last:border-b-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {question.id}. {question.text}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {scoreOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(question.id, option.value)}
                        className={`p-4 rounded-lg border-2 transition-all text-center ${
                          answers[question.id] === option.value
                            ? 'border-[#4A90E2] bg-blue-50 text-[#4A90E2] font-semibold'
                            : 'border-gray-200 hover:border-[#48C9B0] text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={handleSubmit}
                disabled={!isComplete}
                className={`inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold transition-all ${
                  isComplete
                    ? 'bg-[#4A90E2] text-white hover:bg-[#3A7BC8] shadow-lg cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isComplete ? (
                  <>
                    Calculate My Score
                    <CheckCircle className="ml-2 w-5 h-5" />
                  </>
                ) : (
                  <>Answer all questions to see results</>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-4">
                {Object.keys(answers).length} of 8 questions answered
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}